import type { ApiHttpClient, RequestOptions } from "../interfaces/api-http-client";
import type {
  UpdateProfileData,
  ChangePasswordData,
  NotificationPreferenceChange,
  NotificationPreferences,
  MetaData,
  CompanyPreferences,
  CompanyPreferencesUpdate,
  VocabularyUpdateData,
  ImageSelectionType,
  AppearancePreferences,
  MenuPreferencesUpdate,
  MediaInsightPreferencesUpdate,
  CompanyNavDefaultsUpdate,
  AccountDeletionAnswer,
  PublicTranscriptionLanguage,
  AssignToItem,
  AIProviderName,
  AIProviderKeyPublic,
  AssistantTemplate,
  AiContextData,
  ProfileNotificationResponse,
} from "./types";

export function createProfileApi(http: ApiHttpClient) {
  return {
    async getProfile(options?: RequestOptions) {
      return http.get<ProfileNotificationResponse>("/user/profile", undefined, options);
    },

    async updateProfile(data: UpdateProfileData) {
      return http.put<{ message: string }>("/user/profile", data);
    },

    async updatePicture(formData: FormData) {
      return http.upload<{ message: string }>("/user/updatePicture", formData);
    },

    async changePassword(data: ChangePasswordData) {
      return http.post<{ status: string; message: string }>(
        "/user/changePassword",
        data,
      );
    },

    async updateNotificationPreferences(preferences: NotificationPreferenceChange[]) {
      return http.put<{ notification: NotificationPreferences }>(
        "/user/notification/preferences",
        { preferences },
      );
    },

    async updateAppearance(data: AppearancePreferences) {
      return http.put<{ status: string; message: string }>("/user/appearance", data);
    },

    async getPreferences() {
      return http.get<CompanyPreferences>("/user/preferences");
    },

    async updatePreferences(data: CompanyPreferencesUpdate) {
      return http.put<{ status: string; message: string }>("/user/preferences", data);
    },

    async updateMenuPreferences(data: MenuPreferencesUpdate) {
      return http.put<{ status: string; message: string }>(
        "/user/menu-preferences",
        data,
      );
    },

    async updateMediaInsightPreferences(data: MediaInsightPreferencesUpdate) {
      return http.put<{ status: string; message: string }>(
        "/user/media-insight-preferences",
        data,
      );
    },

    async updateCompanyNavDefaults(data: CompanyNavDefaultsUpdate) {
      return http.put<{ status: string; message: string }>(
        "/user/company-nav-defaults",
        data,
      );
    },

    async updateVocabulary(data: VocabularyUpdateData) {
      return http.put<{ status: string; message: string }>(
        "/user/preferences/vocabulary",
        data,
      );
    },

    async updatePlatformLanguage(language: string) {
      return http.put<{ status: string; message: string }>("/user/platform-language", {
        language,
      });
    },

    async getCustomization() {
      return http.get<MetaData>("/user/metaData");
    },

    async updateCustomization(data: MetaData) {
      return http.put<{ status: string; message: string }>(
        "/user/customizationSetting",
        data,
      );
    },

    async updateMetaImage(type: ImageSelectionType, formData: FormData) {
      return http.upload<{ message: string }>(`/user/meta/${type}`, formData);
    },

    async removeMetaImage(type: ImageSelectionType) {
      return http.delete<{ status: string }>(`/user/meta/${type}`);
    },

    async requestDataExport(feedback: string) {
      return http.post<{ status: string; message: string }>("/user/exportAccount", {
        feedback,
      });
    },

    async requestAccountDeletion(reason: string, questions: AccountDeletionAnswer[]) {
      return http.post<{ status: string; message: string }>("/user/delete", {
        reason,
        questions,
      });
    },

    async updateDeleteDataDuration(data: { duration: number }) {
      return http.put<{ status: string; message: string }>(
        "/user/delete/duration",
        data,
      );
    },

    async checkLinkAccounts() {
      return http.get<{ isGoogle: boolean }>("/user/linkAccounts");
    },

    async disconnectAccount(type: string) {
      return http.post<{ message: string }>(`/user/disconnect/${type}`, {});
    },

    async getTaxInfo() {
      return http.get<Record<string, unknown>>("/user/tax");
    },

    async updateTaxInfo(data: Record<string, unknown>) {
      return http.put<{ status: string; message: string }>("/user/tax", data);
    },

    async getTranscriptionLanguages() {
      return http.get<PublicTranscriptionLanguage[]>("/public/transcription-languages");
    },

    async checkCalendarPermission() {
      return http.get<{ status: boolean }>("/user/calendarPermission");
    },

    async getAssignToList() {
      return http.get<AssignToItem[]>("/user/assignToList");
    },

    /** 404s when the company lacks BYOK. That 404 IS the gate; the client carries no separate flag. */
    async getProviderKeys() {
      return http.get<AIProviderKeyPublic[]>("/user/ai-provider-keys");
    },

    async saveProviderKey(provider: AIProviderName, data: { key: string; label?: string }) {
      return http.put<AIProviderKeyPublic>(`/user/ai-provider-keys/${provider}`, data);
    },

    async removeProviderKey(provider: AIProviderName) {
      return http.delete<{ provider: AIProviderName; deleted: boolean }>(
        `/user/ai-provider-keys/${provider}`,
      );
    },

    /** Server returns the templates doc `{ assistants }` when one exists, or a bare `[]` when none. */
    async getAssistantTemplates() {
      return http.get<AssistantTemplate[] | { assistants?: AssistantTemplate[] }>(
        "/assistant-templates",
      );
    },

    /** prompts must be echoed back: the server $sets it from the body, so omitting it wipes existing prompts. */
    async createAiContext(data: AiContextData) {
      return http.put<{ status: string }>("/assistant-templates", data);
    },

    async updateAiContext(id: string, data: AiContextData) {
      return http.put<{ status: string }>("/assistant-templates", { id, ...data });
    },

    async deleteAiContext(id: string) {
      return http.delete<{ message: string }>(`/assistant-templates/${id}`);
    },
  };
}
